import { gatewayClient } from '../utils/gatewayClient';

interface MembershipInfo {
  city?: string;
  dob: string;
  employee_id: string;
  excluded_wallet_ids: string[];
  is_current_member: string;
  is_dna_profile_present: string;
  locality?: string;
  phone: string;
  pid: string;
  product_id: string;
  sales_tax: number;
  state?: string;
  street?: string;
  street_ext?: string;
  tos: string;
  type: string;
  use_points: boolean;
  vendor_code: string;
  vid: string;
  zip?: string;
}

export interface FetchSubscriptionsResponse {
  count: number;
  limit: number;
  page: number;
  subscriptions: Array<{
    company_id: string;
    company_name: string;
    created: string;
    employee_email: string;
    employee_first_name: string;
    employee_full_name: string;
    employee_id: string;
    employee_last_name: string;
    excluded_wallet_ids: string[];
    id: string;
    membership_info: MembershipInfo;
    next_bill_date: string;
    product_id: string;
    product_options?: string;
    product_title: string;
    status: string;
    title: string;
    total_amount: number;
    use_points: boolean;
    vendor_id: string;
    vendor_title: string;
  }>;
}

export function fetchSubscriptions(params: {
  page?: number;
  limit?: number;
  sort?: string;
  status?: string;
  vendor_id?: string;
  product_id?: string;
}) {
  return gatewayClient.get<FetchSubscriptionsResponse>(
    '/api/v2/subscriptions',
    {
      params,
    }
  );
}

interface Product {
  categories: string[];
  checkout?: {
    is_subscription: boolean;
    shipping_required: boolean;
    sponsored_amount: number;
  };
  description: string;
  digital_app_display?: {
    banner_image_url: string;
    icon_image_url: string;
    platforms: string[];
    description: string;
    callouts: {
      title: string;
      description: string;
      image_url: string;
    }[];
    highlights: string[];
  };
  eligible_wallet_configuration_ids: string[];
  eligible_wallets: string[];
  external_checkout_url?: string;
  id: string;
  instruction_image?: string[];
  is_inactive: boolean;
  is_sales_tax_required: boolean;
  msrp: number;
  name?: string;
  shipping_required: boolean;
  special_price: number;
  subcategories: string[];
  tags: string[];
  term: string;
  title: string;
}

interface Vendor {
  categories: string[];
  code_name: string;
  cancellation_refund?: string;
  coupon_code_available: boolean;
  coupon_code_locations: string[];
  description: string;
  faqs?: {
    q: string;
    a: string;
  }[];
  id: string;
  image_url: string;
  internal_tags: string[];
  is_checkout_enabled: boolean;
  name: string;
  pricing_plan_available: boolean;
  product_list: Product[];
  proration_allowed: boolean;
  special_terms?: string;
  subcategories: string[];
  subtitle: string;
  title: string;
  type: string;
  website_url: string;
  what_to_expect?: string;
}

export interface FetchSubscriptionResponse {
  subscription: {
    company_id: string;
    company_name: string;
    created: string;
    employee_email: string;
    employee_first_name: string;
    employee_full_name: string;
    employee_id: string;
    employee_last_name: string;
    excluded_wallet_ids: string[];
    id: string;
    membership_info: MembershipInfo;
    next_bill_date: string;
    product: Product;
    product_id: string;
    product_options?: string;
    product_title: string;
    status: string;
    title: string;
    total_amount: number;
    use_points: boolean;
    vendor: Vendor;
    vendor_id: string;
    vendor_title: string;
  };
}

export function fetchSubscriptionById(id: string) {
  return gatewayClient.get<FetchSubscriptionResponse>(
    `/api/v2/subscriptions/${id}`
  );
}

export function cancelSubscription(id: string) {
  return gatewayClient.put(`/api/v2/subscriptions/${id}/cancel`);
}

export function updateSubscription({
  id,
  excluded_wallet_ids,
  use_points,
}: {
  id: string;
  excluded_wallet_ids?: Array<string>;
  use_points?: boolean;
}) {
  return gatewayClient.put(`/api/v2/subscriptions/${id}`, {
    excluded_wallet_ids,
    use_points,
  });
}
